(ns app.front.view.admin.common
  (:require [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [clojure.string :as str]
            [core.front.admin]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::authority-domain-groups
  :<- [::entities/entities {:group/type    :group.type/domain
                            :group/subtype :group.subtype/local-government}]
  (fn [groups]
    (filter #(some group-model/application-or-upper-tier-authority? (:entity/_parent %)) groups)))


(rf/reg-sub ::domain-groups-of-authorities
  :<- [::authority-domain-groups]
  (fn [groups]
    (sort-by :group/name groups)))


(rf/reg-sub ::domain-groups-for-translation-mappings
  :<- [::authority-domain-groups]
  (fn [groups]
    (->> (map group-model/inplace-superdomain groups)
         set
         (sort-by :group/name))))


(defn entity-id? [v]
  (and (integer? v)
       (>= (count (str v)) 14)))


(defn member-editor [{:keys [tr groups current-group-ids add-title light save-evt remove-evt save-fn remove-fn val->str no-pills?]}]
  (r/with-let [*adding?      (r/atom false)
               *new-group-id (r/atom nil)]
    (let [selectable-group-ids (->> groups
                                    (map :db/id)
                                    (remove #(contains? current-group-ids %)))]
      [:div
       (when-not no-pills?
         [:div.mb-4.flex.flex-wrap.gap-y-2
          (for-frag [group-db-id current-group-ids
                     :let [group (p/find-first groups {:db/id group-db-id})]]
            [ui-config-pills/removable-pill
             {:tr        tr
              :on-remove #(if remove-evt
                            (>evt (conj remove-evt group-db-id))
                            (remove-fn group-db-id))}
             (val->str group)])])
       (if @*adding?
         [:div.flex
          [ui-select/select {:id           "lower-tier-authority-group-select"
                             :class-name   "full-width-select"
                             :label-text   (tr [:municipalities/municipality])
                             :hide-label   true
                             :allow-empty? false
                             :light        (boolean light)
                             :value        @*new-group-id
                             :values       selectable-group-ids
                             :on-change    #(reset! *new-group-id %)
                             :val->str     #(-> groups
                                                (p/find-first {:db/id %})
                                                val->str)}]
          [ui-config-pills/add-cancel-buttons
           (util/mmap {:on-cancel #(do (reset! *adding? false)
                                       (reset! *new-group-id nil))
                       :on-add    #(do (if save-evt
                                         (>evt (conj save-evt @*new-group-id))
                                         (save-fn @*new-group-id))
                                       (reset! *new-group-id nil)
                                       (reset! *adding? false))
                       :disabled? (nil? @*new-group-id)}
                      tr)]]
         (cc/button {:kind     :tertiary
                     :icon     i/plus
                     :size     :field
                     :on-click #(reset! *adding? true)}
                    (tr [add-title])))])))


(defn member-group-editor [opts]
  (member-editor (assoc opts :val->str :group/name)))

(rf/reg-sub ::data :-> ::data)

(rf/reg-event-db ::combo-input-changed
  (fn [db [_ value]]
    (assoc-in db [::data ::combo-input] value)))

(rf/reg-sub ::current-input-text
  :<- [::data]
  :-> ::combo-input)

(rf/reg-sub ::groups-for-dropdown
  (fn [[_ groups-subscription]]
    (rf/subscribe (or groups-subscription [:core.front.admin/groups])))
  (fn [groups [_ _ skip-sorting?]]
    (cond->> (map (fn [{:group/keys [id name] :as group}]
                    {:label           (str name " (" id
                                           (when (group-model/upper-tier-authority? group)
                                             ", Upper-tier")
                                           ")")
                     :id              (:db/id group)
                     :group-id        (:group/id group)
                     :uri-path-id     (:group/uri-path-id group)
                     :has-workspaces? (:domain-group/has-workspaces? group)
                     :has-complaints? (:domain-group/has-complaints? group)})
                  groups)
      ;; groups-subscription can have own sorting
      (not skip-sorting?) (sort-by :label))))

(rf/reg-sub ::filtered-groups-for-dropdown
  (fn [[_ groups-subscription skip-sorting?]]
    [(rf/subscribe [::groups-for-dropdown groups-subscription skip-sorting?])
     (rf/subscribe [::current-input-text])])
  (fn [[groups input-text] [_ _ skip-sorting?]]
    (let [lc-input (some-> input-text str str/lower-case)]
      (cond->> (filter (fn [{:keys [label]}]
                         (or (str/blank? lc-input)
                             (str/includes? (str/lower-case label) lc-input)))
                       groups)
        ;; groups-subscription can have own sorting
        (not skip-sorting?) (sort-by :label)))))

(rf/reg-event-fx ::set-selected-group
  (fn [{:keys [db]} [_ group]]
    {:db (assoc-in db [::data ::selected-group] group)}))

(rf/reg-sub ::selected-group
  :<- [::data]
  :-> ::selected-group)

(defn parse-change [^js evt]
  (-> evt
      .-selectedItem
      (js->clj :keywordize-keys true)))

(defn group-selector [{:keys [tr on-change groups-subscription skip-sorting?] :as opts}]
  (let [base-opts   {:id              "dashboard-group-for-config-dropdown"
                     :on-change       #(let [c (parse-change %)]
                                         (>evt [::set-selected-group c])
                                         (when on-change
                                           (on-change c)))
                     :placeholder     (tr [:dashboard-group-config/select-group])
                     :items           (<sub [::filtered-groups-for-dropdown
                                             groups-subscription
                                             skip-sorting?])
                     :on-input-change #(rf/dispatch-sync [::combo-input-changed %])}
        passed-opts (dissoc opts :tr :on-change :groups-subscription :skip-sorting?)]
    (cc/combo-box (merge base-opts passed-opts))))
